'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Zap, Sun, Moon, Menu, X } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navTextColor = theme === 'light' ? '#1B2A4A' : 'rgba(255,255,255,0.85)';
  const toggleBg = theme === 'light' ?'rgba(27,42,74,0.08)' :'rgba(255,255,255,0.08)';
  const toggleHoverBg = theme === 'light' ?'rgba(27,42,74,0.14)' :'rgba(255,255,255,0.14)';

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? 'var(--nav-bg-scrolled)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : 'none',
      }}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
        {/* LEFT: Logo */}
        <Link href="/" className="flex items-center flex-shrink-0">
          <img
            src="/assets/images/logo-removebg-preview_2_-1778277745384.png"
            alt="ECHOMINT"
            style={{ height: 48, width: 'auto', objectFit: 'contain' }}
            className="drop-shadow-lg sm:h-16"
          />
        </Link>

        {/* RIGHT: Desktop Navigation */}
        <div className="hidden sm:flex items-center gap-2">
          {/* Theme toggle — always visible in both modes */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg transition-all duration-200"
            aria-label="Toggle theme"
            style={{
              color: navTextColor,
              background: toggleBg,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = toggleHoverBg; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = toggleBg; }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <Link
            href="/sign-up-login-screen"
            className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150"
            style={{ color: navTextColor }}
          >
            Sign In
          </Link>
          <Link
            href="/pulse"
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-150 hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #A855F7, #2A9D8F)', color: '#fff' }}
          >
            <Zap size={14} />
            Get on PULSE
          </Link>
        </div>

        {/* Mobile: theme toggle + hamburger */}
        <div className="flex sm:hidden items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg transition-all duration-200"
            aria-label="Toggle theme"
            style={{
              color: navTextColor,
              background: toggleBg,
            }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 rounded-lg transition-all duration-150"
            aria-label="Toggle menu"
            style={{ color: navTextColor, background: toggleBg }}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="sm:hidden px-4 pb-4 flex flex-col gap-2"
          style={{
            background: 'var(--nav-bg-scrolled)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Link
            href="/sign-up-login-screen"
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 text-sm font-medium rounded-lg transition-all duration-150 text-center"
            style={{ color: navTextColor }}
          >
            Sign In
          </Link>
          <Link
            href="/pulse"
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-lg transition-all duration-150 hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #A855F7, #2A9D8F)', color: '#fff' }}
          >
            <Zap size={14} />
            Get on PULSE
          </Link>
        </div>
      )}
    </nav>
  );
}